import { ref } from 'vue'
import { apiClient } from '@/api/client'

/** Un centro come serve ai selettori: id e nome, più il codice quando il centro ne ha uno. */
export interface MioCentro {
  id: number
  nome: string
  codice?: string
}

/** Etichetta «codice - nome» come nel classico; solo il nome se il centro non ha codice. */
export function etichettaCentro(c: Pick<MioCentro, 'nome' | 'codice'>): string {
  return c.codice ? `${c.codice} - ${c.nome}` : c.nome
}

/**
 * I centri a cui l'utente è abilitato — `GET /api/v2/centri/miei`.
 *
 * È l'elenco giusto per ogni pagina che chiede «in quale centro?»: protocollo, posta, ordini,
 * documenti, diario. NON l'anagrafica dei centri (`GET /api/v2/centri`), che richiede il permesso
 * di gestirla: chi protocolla o ordina di solito non ce l'ha, e leggendo da lì vedeva il menu
 * vuoto senza capire perché.
 *
 * Il risultato è condiviso fra le pagine per tutta la sessione: i centri di una persona non
 * cambiano mentre lavora, e così ogni dialog che si apre non rifà la stessa chiamata.
 */
const cache = ref<MioCentro[] | null>(null)
let inCorso: Promise<MioCentro[]> | null = null
/** Solo i centri con gli ospiti (niente magazzini né centri solo personale), attivi: cache a parte. */
const cacheOspiti = ref<MioCentro[] | null>(null)
let inCorsoOspiti: Promise<MioCentro[]> | null = null

/**
 * Come {@link useMieiCentri}, ma solo i centri dell'Elenco Centri (quelli con gli ospiti) ancora attivi:
 * `GET /api/v2/centri/miei?soloOspiti=true&soloAttivi=true`. Per i selettori dove un magazzino non ha senso.
 */
export function useMieiCentriOspiti() {
  const centri = ref<MioCentro[]>(cacheOspiti.value ?? [])
  const loading = ref(false)
  async function carica(): Promise<MioCentro[]> {
    if (cacheOspiti.value) {
      centri.value = cacheOspiti.value
      return cacheOspiti.value
    }
    loading.value = true
    try {
      inCorsoOspiti ??= apiClient
        .get<Array<{ id: number; nome: string | null; codice?: string | null }>>('/api/v2/centri/miei',
          { params: { soloOspiti: true, soloAttivi: true } })
        .then(({ data }) => (data ?? []).map((c) => ({ id: c.id, nome: c.nome ?? `Centro ${c.id}`, codice: c.codice ?? undefined })))
      const elenco = await inCorsoOspiti
      cacheOspiti.value = elenco
      centri.value = elenco
      return elenco
    } catch {
      centri.value = []
      return []
    } finally {
      inCorsoOspiti = null
      loading.value = false
    }
  }
  return { centri, loading, carica }
}

export function useMieiCentri() {
  const centri = ref<MioCentro[]>(cache.value ?? [])
  const loading = ref(false)
  /** Messaggio da mostrare se l'elenco non è arrivato: mai una tendina vuota e muta. */
  const errore = ref<string | null>(null)

  async function carica(forza = false): Promise<MioCentro[]> {
    if (cache.value && !forza) {
      centri.value = cache.value
      return cache.value
    }
    loading.value = true
    errore.value = null
    try {
      inCorso ??= apiClient
        .get<Array<{ id: number; nome: string | null; codice?: string | null }>>('/api/v2/centri/miei')
        .then(({ data }) => (data ?? []).map((c) => ({ id: c.id, nome: c.nome ?? `Centro ${c.id}`, codice: c.codice ?? undefined })))
      const elenco = await inCorso
      cache.value = elenco
      centri.value = elenco
      return elenco
    } catch {
      errore.value = 'Non è stato possibile caricare i tuoi centri: ricarica la pagina, '
        + 'e se il problema resta contatta l\'assistenza.'
      centri.value = []
      return []
    } finally {
      inCorso = null
      loading.value = false
    }
  }

  return { centri, loading, errore, carica }
}

/** Da chiamare al logout o al cambio utente, perché i centri sono della persona. */
export function dimenticaMieiCentri(): void {
  cache.value = null
  cacheOspiti.value = null
}
